import type { ReactNode, SVGProps } from "react";

import { GitHubIcon } from "../Icons";
import type { ForgePluginId } from "./pluginsPage.logic";

interface PluginIconProps extends SVGProps<SVGSVGElement> {
  readonly pluginId: ForgePluginId;
}

type BrandIcon = (props: SVGProps<SVGSVGElement>) => ReactNode;

const GmailLogo: BrandIcon = (props) => (
  <svg {...props} viewBox="0 0 1000 1000" role="img" aria-hidden="true">
    <path
      fill="#ef463d"
      d="M284.1 263.2c34.7 25.9 69.5 51.7 104.2 77.7 33.4 25 66.8 49.9 100 75.2 5 3.8 8.1 4 13.3.1 65.7-49.6 131.6-98.9 197.5-148.3 2.2-1.6 4.5-3 6.8-4.6.1 4.4.2 8.8.2 13.2.1 71.4.1 142.8.2 214.2-.2.5-.3.9-.5 1.4-27.7 20.6-55.5 41.1-83.1 61.8-40.4 30.2-80.8 60.5-121 91-5.1 3.8-8.2 3.8-13.2-.1-66.3-50.1-132.8-99.8-199.3-149.7-1.5-1.1-3.2-2.1-4.8-3.1-.2-.5-.3-1-.4-1.5 0-75.8.1-151.6.1-227.3"
    />
    <path
      fill="#427cbf"
      d="M284 490.5c.1.5.2 1 .4 1.5-.1 31-.2 62.1-.3 93.1 0 65.6-.1 131.3.1 196.9 0 6.2-1.2 8.5-8 8.4-37.9-.4-75.8-.1-113.7-.2-30.9-.1-54.3-22.9-54.4-53.2-.1-124.4 0-248.9 0-373.3 0-1.4.2-2.7.3-4.1 2.9 2 5.9 4 8.7 6.1 55.7 41.6 111.3 83.2 166.9 124.8"
    />
    <path
      fill="#00af59"
      d="M705.8 492.1c.2-.5.3-.9.5-1.4 13.6-10.1 27.3-20.2 40.9-30.4 44.8-33.5 89.6-67 134.4-100.4.1 3.3.4 6.6.4 9.9v363.3c0 35.1-21.9 57.2-56.9 57.2-36.5 0-73-.3-109.6.2-8.3.1-9.7-2.4-9.6-10.1.3-92.8.2-185.6.2-278.5 0-3.3-.2-6.5-.3-9.8"
    />
    <path
      fill="#dc2226"
      d="M284 490.5c-55.6-41.6-111.2-83.2-166.8-124.8-2.8-2.1-5.8-4.1-8.7-6.1 0-26.8-2.1-53.8.8-80.5 6.3-57.5 71.9-88.5 120.4-56.7 18.9 12.4 36.4 27.1 54.5 40.7-.1 75.8-.2 151.6-.2 227.4"
    />
    <path
      fill="#fdb716"
      d="M881.5 359.8c-44.8 33.5-89.6 67-134.4 100.4-13.6 10.2-27.3 20.3-40.9 30.4-.1-71.4-.1-142.8-.2-214.2 0-4.4-.1-8.8-.2-13.2 18.1-13.7 35.6-28.4 54.5-40.9 50.5-33.3 117.9 1.5 121.3 62.6 1.5 25 .1 50-.1 74.9"
    />
  </svg>
);

const GoogleCalendarLogo: BrandIcon = (props) => (
  <svg {...props} viewBox="0 0 48 48" role="img" aria-hidden="true">
    <path
      fill="#fff"
      d="M9 10h30c1.7 0 3 1.3 3 3v26c0 1.7-1.3 3-3 3H9c-1.7 0-3-1.3-3-3V13c0-1.7 1.3-3 3-3Z"
    />
    <path fill="#4285f4" d="M9 6h30c1.7 0 3 1.3 3 3v8H6V9c0-1.7 1.3-3 3-3Z" />
    <path fill="#34a853" d="M6 17h9v25H9c-1.7 0-3-1.3-3-3V17Z" />
    <path fill="#fbbc04" d="M33 17h9v22c0 1.7-1.3 3-3 3h-6V17Z" />
    <path fill="#ea4335" d="M15 17h18v25H15V17Z" />
    <path fill="#fff" d="M15 17h18v19H15V17Z" />
    <path
      fill="#4285f4"
      d="M23.5 33.5c-1.8 0-3.2-.7-4.3-1.8l1.7-1.7c.7.8 1.5 1.2 2.6 1.2 1.3 0 2.2-.7 2.2-1.8 0-1.2-1-1.8-2.5-1.8h-1.1v-2.2h1.1c1.3 0 2.2-.6 2.2-1.7 0-.9-.8-1.5-1.9-1.5-.9 0-1.6.4-2.2 1.1l-1.7-1.7c1-1.1 2.3-1.7 4-1.7 2.5 0 4.3 1.4 4.3 3.5 0 1.3-.8 2.3-2 2.8 1.4.4 2.4 1.5 2.4 3.1 0 2.4-2 4-4.8 4Z"
    />
    <path fill="#4285f4" d="M31.5 33.2V23l-2.4 1.1v-2.4l3.1-1.5h1.9v13h-2.6Z" />
  </svg>
);

const GitHubLogo: BrandIcon = (props) => <GitHubIcon {...props} />;

// Forge-owned capability rather than a third-party brand, so it uses the
// product's line-icon language instead of a vendor logo. Inline like the
// sibling icons, and stroked with currentColor so it themes automatically.
const ComputerUseLogo: BrandIcon = (props) => (
  <svg
    {...props}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.75}
    strokeLinecap="round"
    strokeLinejoin="round"
    role="img"
    aria-hidden="true"
  >
    <rect x="2" y="3" width="20" height="14" rx="2" />
    <path d="M8 21h8M12 17v4" />
  </svg>
);

const pluginIconById: Readonly<Record<ForgePluginId, BrandIcon>> = {
  gmail: GmailLogo,
  "google-calendar": GoogleCalendarLogo,
  github: GitHubLogo,
  "computer-use": ComputerUseLogo,
};

export function PluginIcon({ pluginId, ...props }: PluginIconProps) {
  const Icon = pluginIconById[pluginId];
  return <Icon {...props} />;
}
